import { useCallback } from "react";
import { useGlobalState } from "senf-shared/index";

/**
 * Custom hook for managing Cesium features in a consistent way.
 * This hook provides a streamlined interface for updating savedFeatures
 * and ensures setStatefulCesiumMap is always the source of truth.
 */
export const useCesiumFeatures = () => {
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;

  /**
   * Updates the savedFeatures in statefulCesiumMap and notifies callbacks
   * @param updatedFeatures - The new feature collection
   * @param callbacks - Optional callbacks to notify of the update
   */
  const updateSavedFeatures = useCallback(
    (
      updatedFeatures: GeoJSON.FeatureCollection,
      callbacks?: {
        onDrawComplete?: (features: GeoJSON.FeatureCollection) => void;
        handleSaveDrawn?: (features: GeoJSON.FeatureCollection) => void;
      },
    ) => {
      // Always update the global state first (single source of truth)
      setStatefulCesiumMap((prev) => ({
        ...prev,
        savedFeatures: updatedFeatures,
      }));

      // Notify callbacks after state update
      if (callbacks?.handleSaveDrawn) {
        callbacks.handleSaveDrawn(updatedFeatures);
      } else if (callbacks?.onDrawComplete) {
        callbacks.onDrawComplete(updatedFeatures);
      }
    },
    [setStatefulCesiumMap],
  );

  /**
   * Adds a new feature to the current savedFeatures
   * @param feature - The feature to add
   * @param callbacks - Optional callbacks to notify of the update
   */
  const addFeature = useCallback(
    (
      feature: GeoJSON.Feature,
      callbacks?: {
        onDrawComplete?: (features: GeoJSON.FeatureCollection) => void;
        handleSaveDrawn?: (features: GeoJSON.FeatureCollection) => void;
      },
    ) => {
      // Validation
      if (!feature || !feature.geometry) {
        console.warn(
          "useCesiumFeatures: Cannot add feature without valid geometry",
        );
        return;
      }

      if (!feature.id && !feature.properties?.id) {
        console.warn("useCesiumFeatures: Feature missing ID, auto-generating");
        feature.id = `feature-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      setStatefulCesiumMap((prevState) => {
        const prevFeatures = prevState?.savedFeatures;
        let updatedFeatures: GeoJSON.FeatureCollection;

        if (!prevFeatures) {
          updatedFeatures = {
            type: "FeatureCollection",
            features: [feature],
          };
        } else {
          updatedFeatures = {
            type: "FeatureCollection",
            features: [...prevFeatures.features, feature],
          };
        }

        // Notify callbacks after state update
        if (callbacks?.handleSaveDrawn) {
          callbacks.handleSaveDrawn(updatedFeatures);
        } else if (callbacks?.onDrawComplete) {
          callbacks.onDrawComplete(updatedFeatures);
        }

        return {
          ...prevState,
          savedFeatures: updatedFeatures,
        };
      });
    },
    [setStatefulCesiumMap],
  );

  /**
   * Removes a feature from savedFeatures
   * @param featureId - The ID of the feature to remove
   * @param callbacks - Optional callbacks to notify of the update
   */
  const removeFeature = useCallback(
    (
      featureId: string,
      callbacks?: {
        onDrawComplete?: (features: GeoJSON.FeatureCollection) => void;
        handleSaveDrawn?: (features: GeoJSON.FeatureCollection) => void;
      },
    ) => {
      // Validation
      if (!featureId) {
        console.warn("useCesiumFeatures: Cannot remove feature without ID");
        return;
      }

      setStatefulCesiumMap((prevState) => {
        const prevFeatures = prevState?.savedFeatures;

        if (!prevFeatures?.features) {
          console.warn("useCesiumFeatures: No features to remove");
          return prevState;
        }

        const updatedFeatures: GeoJSON.FeatureCollection = {
          ...prevFeatures,
          features: prevFeatures.features.filter(
            (f) => f.id !== featureId && f.properties?.id !== featureId,
          ),
        };

        // Notify callbacks after state update
        if (callbacks?.handleSaveDrawn) {
          callbacks.handleSaveDrawn(updatedFeatures);
        } else if (callbacks?.onDrawComplete) {
          callbacks.onDrawComplete(updatedFeatures);
        }

        return {
          ...prevState,
          savedFeatures: updatedFeatures,
        };
      });
    },
    [setStatefulCesiumMap],
  );

  /**
   * Updates properties of a specific feature
   * @param featureId - The ID of the feature to update
   * @param properties - The properties to update
   * @param callbacks - Optional callbacks to notify of the update
   */
  const updateFeatureProperties = useCallback(
    (
      featureId: string,
      properties: Record<string, unknown>,
      callbacks?: {
        onDrawComplete?: (features: GeoJSON.FeatureCollection) => void;
        handleSaveDrawn?: (features: GeoJSON.FeatureCollection) => void;
      },
    ) => {
      // Validation
      if (!featureId) {
        console.warn("useCesiumFeatures: Cannot update feature without ID");
        return;
      }

      if (!properties || Object.keys(properties).length === 0) {
        console.warn("useCesiumFeatures: No properties to update");
        return;
      }

      setStatefulCesiumMap((prevState) => {
        const prevFeatures = prevState?.savedFeatures;

        if (!prevFeatures?.features) {
          console.warn("useCesiumFeatures: No features to update");
          return prevState;
        }

        const updatedFeatures: GeoJSON.FeatureCollection = {
          ...prevFeatures,
          features: prevFeatures.features.map((f) => {
            if (f.id === featureId || f.properties?.id === featureId) {
              return {
                ...f,
                properties: {
                  ...f.properties,
                  ...properties,
                },
              };
            }
            return f;
          }),
        };

        // Notify callbacks after state update
        if (callbacks?.handleSaveDrawn) {
          callbacks.handleSaveDrawn(updatedFeatures);
        } else if (callbacks?.onDrawComplete) {
          callbacks.onDrawComplete(updatedFeatures);
        }

        return {
          ...prevState,
          savedFeatures: updatedFeatures,
        };
      });
    },
    [setStatefulCesiumMap],
  );

  /**
   * Updates vertex position for geometry editing (drag operations)
   * @param featureId - The ID of the feature to update
   * @param vertexIndex - The index of the vertex to update
   * @param newPosition - The new Cartesian3 position
   * @param callbacks - Optional callbacks to notify of the update
   */
  const updateVertexPosition = useCallback(
    (
      featureId: string,
      vertexIndex: number,
      newPosition: number[], // Should be a coordinate array [lng, lat, height]
      callbacks?: {
        onDrawComplete?: (features: GeoJSON.FeatureCollection) => void;
        handleSaveDrawn?: (features: GeoJSON.FeatureCollection) => void;
      },
    ) => {
      // Use a functional update to ensure we're always working with the latest state
      setStatefulCesiumMap((prevState) => {
        const prevFeatures = prevState?.savedFeatures;

        if (!prevFeatures?.features) {
          console.warn("useCesiumFeatures: No features to update");
          return prevState;
        }

        const featureToUpdate = prevFeatures.features.find(
          (f) => f.id === featureId || f.properties?.id === featureId,
        );

        if (!featureToUpdate?.geometry) {
          console.warn(
            `useCesiumFeatures: Feature ${featureId} not found or has no geometry`,
          );
          return prevState;
        }
        if (!newPosition || !Array.isArray(newPosition)) {
          console.warn(
            "useCesiumFeatures: Invalid position format, expected a coordinate array",
          );
          return prevState;
        }

        const newCoordinate = newPosition;
        const geometryType = featureToUpdate.geometry.type;
        const updatedGeometry = JSON.parse(
          JSON.stringify(featureToUpdate.geometry),
        ); // Deep clone

        if (geometryType === "Point") {
          updatedGeometry.coordinates = newCoordinate;
        } else if (
          geometryType === "LineString" &&
          "coordinates" in updatedGeometry
        ) {
          if (vertexIndex < updatedGeometry.coordinates.length) {
            updatedGeometry.coordinates[vertexIndex] = newCoordinate;
          }
        } else if (
          geometryType === "Polygon" &&
          "coordinates" in updatedGeometry
        ) {
          if (
            updatedGeometry.coordinates[0] &&
            vertexIndex < updatedGeometry.coordinates[0].length
          ) {
            updatedGeometry.coordinates[0][vertexIndex] = newCoordinate;
            if (
              vertexIndex === 0 &&
              updatedGeometry.coordinates[0].length > 1
            ) {
              updatedGeometry.coordinates[0][
                updatedGeometry.coordinates[0].length - 1
              ] = newCoordinate;
            }
          }
        }

        const updatedFeature = {
          ...featureToUpdate,
          geometry: updatedGeometry,
        };

        const updatedFeatures: GeoJSON.FeatureCollection = {
          ...prevFeatures,
          features: prevFeatures.features.map((f) =>
            f.id === featureId || f.properties?.id === featureId
              ? updatedFeature
              : f,
          ),
        };

        // Notify callbacks after state update
        if (callbacks?.handleSaveDrawn) {
          callbacks.handleSaveDrawn(updatedFeatures);
        } else if (callbacks?.onDrawComplete) {
          callbacks.onDrawComplete(updatedFeatures);
        }

        // Return the new state
        return {
          ...prevState,
          savedFeatures: updatedFeatures,
        };
      });
    },
    [setStatefulCesiumMap],
  );

  /**
   * Initializes savedFeatures from a feature collection (typically on component mount)
   * @param featureCollection - The initial feature collection
   */
  const initializeSavedFeatures = useCallback(
    (featureCollection: GeoJSON.FeatureCollection) => {
      setStatefulCesiumMap((prev) => {
        // Only initialize if savedFeatures is empty or null
        if (
          !prev?.savedFeatures ||
          !prev.savedFeatures.features ||
          prev.savedFeatures.features.length === 0
        ) {
          return {
            ...prev,
            savedFeatures: featureCollection,
          };
        }
        // Otherwise, return the existing state
        return prev;
      });
    },
    [setStatefulCesiumMap],
  );

  /**
   * Clears all savedFeatures
   */
  const clearSavedFeatures = useCallback(() => {
    setStatefulCesiumMap((prev) => ({
      ...prev,
      savedFeatures: null,
    }));
  }, [setStatefulCesiumMap]);

  /**
   * Gets the current savedFeatures from statefulCesiumMap
   * This is the single source of truth
   */
  const getSavedFeatures = useCallback((): GeoJSON.FeatureCollection | null => {
    return statefulCesiumMap?.savedFeatures || null;
  }, [statefulCesiumMap?.savedFeatures]);

  /**
   * Gets the next available number for a new feature
   */
  const getNextFeatureNumber = useCallback((): number => {
    const existingNumbers = (statefulCesiumMap?.savedFeatures?.features || [])
      .map((f) => f.properties?.number)
      .filter((num) => typeof num === "number");

    return existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  }, [statefulCesiumMap?.savedFeatures]);

  return {
    // State
    savedFeatures: statefulCesiumMap?.savedFeatures,
    viewer: statefulCesiumMap?.viewer,

    // Actions
    updateSavedFeatures,
    addFeature,
    removeFeature,
    updateFeatureProperties,
    updateVertexPosition,
    initializeSavedFeatures,
    clearSavedFeatures,
    getSavedFeatures,
    getNextFeatureNumber,
  };
};
